import React, { useState } from 'react'
import { useImportInterventionInputsMutation } from '../../store/is/interventionInputApi'
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../ui/card"
import { UploadIcon, AlertCircleIcon, CheckCircleIcon } from 'lucide-react'
import { Progress } from "../ui/progress"

interface InterventionInputImportProps {
  onSuccess: () => void
  onCancel: () => void
}

const InterventionInputImport: React.FC<InterventionInputImportProps> = ({
  onSuccess,
  onCancel
}) => {
  const [file, setFile] = useState<File | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<{
    total: number
    success: number
    failed: number
    errors?: string[]
  } | null>(null)

  const [importInputs] = useImportInterventionInputsMutation()

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0])
    }
  }

  const processCSV = async (file: File): Promise<File[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const csv = e.target?.result as string;
          const lines = csv.split('\n').filter(line => line.trim() !== '');
          
          if (lines.length === 0) {
            reject(new Error('CSV file is empty'));
            return;
          }
          
          // Use a proper CSV parsing function to handle quoted fields
          const parseCSVLine = (line: string) => {
            const result = [];
            let inQuotes = false;
            let currentValue = '';
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                result.push(currentValue.trim());
                currentValue = '';
              } else {
                currentValue += char;
              }
            }
            
            // Add the last value
            result.push(currentValue.trim());
            return result;
          };
          
          const originalHeaders = parseCSVLine(lines[0]);
          
          // Group rows by input_type
          const rowsByType: Record<string, any[]> = {
            conventional_fertilizer: [],
            low_carbon_fertilizer: [],
            nitrogen_stabilized_fertilizer: [],
            controlled_release_fertilizer: [],
            fuel: []
          };
          
          // Parse all rows into objects
          for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            const row: Record<string, string> = {};
            
            originalHeaders.forEach((header, index) => {
              if (index < values.length && values[index].trim() !== '') {
                // Remove quotes from values if present
                let value = values[index].trim();
                if (value.startsWith('"') && value.endsWith('"')) {
                  value = value.substring(1, value.length - 1);
                }
                row[header] = value;
              }
            });
            
            // Debug 
            if (row['input_type'] === 'nitrogen_stabilized_fertilizer') {
              console.log('NSF row:', row);
              console.log('Has stabilizer_type in headers?', originalHeaders.includes('stabilizer_type'));
              console.log('Stabilizer value from CSV:', values[originalHeaders.indexOf('stabilizer_type')]);
            }
            
            if (['conventional_fertilizer', 'low_carbon_fertilizer', 'nitrogen_stabilized_fertilizer', 'controlled_release_fertilizer'].includes(row['input_type'])) {
              console.log(`${row['input_type']} row:`, row);
              console.log('Has crop in headers?', originalHeaders.includes('crop'));
              if (originalHeaders.includes('crop')) {
                console.log('Crop value from CSV:', values[originalHeaders.indexOf('crop')]);
              }
            }
            
            const inputType = row['input_type'];
            if (inputType && inputType in rowsByType) {
              rowsByType[inputType].push(row);
            }
          }
          
          // Define exactly which headers to include for each type
          const headersByType = {
            conventional_fertilizer: [
              'input_code', 'input_name', 'input_type', 'emission_factor', 'emission_factor_unit', 
              'input_source', 'year', 'region', 'status', 'version',
              'fertilizer_type', 'nitrogen_content', 'crop'
            ],
            low_carbon_fertilizer: [
              'input_code', 'input_name', 'input_type', 'emission_factor', 'emission_factor_unit', 
              'input_source', 'year', 'region', 'status', 'version',
              'fertilizer_type', 'nitrogen_content', 'crop'
            ],
            nitrogen_stabilized_fertilizer: [
              'input_code', 'input_name', 'input_type', 'emission_factor', 'emission_factor_unit', 
              'input_source', 'year', 'region', 'status', 'version',
              'fertilizer_type', 'nitrogen_content', 'stabilizer_type', 'crop'
            ],
            controlled_release_fertilizer: [
              'input_code', 'input_name', 'input_type', 'emission_factor', 'emission_factor_unit', 
              'input_source', 'year', 'region', 'status', 'version',
              'fertilizer_type', 'nitrogen_content', 'crf_blend_percent', 'crop'
            ],
            fuel: [
              'input_code', 'input_name', 'input_type', 'emission_factor', 'emission_factor_unit', 
              'input_source', 'year', 'region', 'status', 'version',
              'fuel_type'
            ]
          };
          
          // Create a separate CSV file for each type
          const files: File[] = [];
          
          Object.entries(rowsByType).forEach(([type, rows]) => {
            if (rows.length === 0) return;
            
            const headers = headersByType[type as keyof typeof headersByType];
            let csvContent = headers.join(',') + '\n';
            
            rows.forEach(row => {
              const rowValues = headers.map(header => {
                const value = row[header] || '';
                
                // Escape values with commas by quoting them
                if (value.includes(',')) {
                  return `"${value}"`;
                }
                return value;
              });
              
              csvContent += rowValues.join(',') + '\n';
              
              // Debug what values are going into the CSV
              if (type === 'nitrogen_stabilized_fertilizer') {
                console.log('NSF headers:', headers);
                console.log('NSF rowValues:', rowValues);
                console.log('Has stabilizer_type in headers?', headers.includes('stabilizer_type'));
                console.log('stabilizer_type value:', row['stabilizer_type'] || 'NOT_FOUND');
              }
              
              // Debug crop field for all fertilizer types
              if (['conventional_fertilizer', 'low_carbon_fertilizer', 'nitrogen_stabilized_fertilizer', 'controlled_release_fertilizer'].includes(type)) {
                console.log(`${type} - Has crop in headers?`, headers.includes('crop'));
                console.log(`${type} - crop value:`, row['crop'] || 'NOT_FOUND');
              }
            });
            
            files.push(new File([csvContent], `${type}_inputs.csv`, { type: 'text/csv' }));
          });
          
          resolve(files);
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error reading file'));
      };
      
      reader.readAsText(file);
    });
  };
  
  const handleImport = async () => {
    if (!file) return;
    
    setIsLoading(true);
    setResult(null);
    
    try {
      let files: File[] = [];
      
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        files = await processCSV(file);
      } else {
        // For non-CSV files, just use as is
        files = [file];
      }
      
      // Submit each file separately
      let totalSuccess = 0;
      let totalFailed = 0;
      let allErrors: string[] = [];
      
      for (const fileToUpload of files) {
        const formData = new FormData();
        formData.append('file', fileToUpload);
        
        const response = await importInputs(formData).unwrap();
        
        totalSuccess += response.successful_imports;
        totalFailed += response.failed_imports;
        if (response.errors) {
          allErrors = [...allErrors, ...response.errors];
        }
      }
      
      setResult({
        total: totalSuccess + totalFailed,
        success: totalSuccess,
        failed: totalFailed,
        errors: allErrors
      });
      
      if (totalFailed === 0) {
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (error) {
      console.error('Import failed:', error);
      setResult({
        total: 0,
        success: 0,
        failed: 1,
        errors: [typeof error === 'string' ? error : 'The import operation failed. Please try again.']
      });
    } finally {
      setIsLoading(false);
    }
  };

  const successPercentage = result?.total ? Math.round((result.success / result.total) * 100) : 0

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>Import Intervention Inputs</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="border-2 border-dashed rounded-lg p-4 text-center">
          <Input
            type="file"
            accept=".csv,.json"
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
            disabled={isLoading}
          />
          <label 
            htmlFor="file-upload" 
            className="flex flex-col items-center justify-center h-32 cursor-pointer"
          >
            <UploadIcon className="h-10 w-10 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              {file ? file.name : 'Click to upload a CSV or JSON file'}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              For fertilizer types, include crop field (optional)
            </p>
            
          </label>
        </div>

        {!file && (
          <div className="text-xs text-muted-foreground mt-4 border rounded-md p-3">
            <p className="font-semibold mb-1">CSV Template Format:</p>
            <p className="mb-1">For Fertilizer:</p>
            <code className="block bg-muted p-2 rounded text-xs overflow-x-auto whitespace-nowrap">
              input_code,input_name,input_type,emission_factor,emission_factor_unit,input_source,year,region,status,version,fertilizer_type,nitrogen_content,crop
            </code>
            <p className="mt-2 mb-1">For Nitrogen Stabilized Fertilizer:</p>
            <code className="block bg-muted p-2 rounded text-xs overflow-x-auto whitespace-nowrap">
              input_code,input_name,input_type,emission_factor,emission_factor_unit,input_source,year,region,status,version,fertilizer_type,nitrogen_content,stabilizer_type,crop
            </code>
          </div>
        )}

        {result && (
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-semibold">{result.total}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Success</p>
                <p className="text-2xl font-semibold text-green-600">{result.success}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Failed</p>
                <p className="text-2xl font-semibold text-red-600">{result.failed}</p>
              </div>
            </div>

            <Progress value={successPercentage} className="h-2" />

            {result.errors && result.errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm mt-4">
                <div className="flex items-center gap-2 text-red-600 mb-2">
                  <AlertCircleIcon className="h-4 w-4" />
                  <p className="font-medium">Import Errors</p>
                </div>
                <ul className="list-disc pl-5 text-sm space-y-1 max-h-32 overflow-y-auto">
                  {result.errors.map((error, index) => (
                    <li key={index} className="text-red-700">{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.failed === 0 && (
              <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm mt-4">
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircleIcon className="h-4 w-4" />
                  <p className="font-medium">All records imported successfully!</p>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-end space-x-2">
        <Button 
          variant="outline" 
          onClick={onCancel} 
          disabled={isLoading}
        >
          Cancel
        </Button>
        
        <Button 
          onClick={handleImport} 
          disabled={!file || isLoading}
          className="relative"
        >
          {isLoading ? 'Importing...' : 'Import'}
          {isLoading && (
            <span className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
          )}
        </Button>
      </CardFooter>
    </Card>
  )
}

export default InterventionInputImport